/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ConsentsAndDeclarationsModuleSpecsFragment } from '../../../../api/fragments/ConsentsAndDeclarationsModuleSpecs';
import {
    UpdateConsentsAndDeclarationsModuleDocument,
    type UpdateConsentsAndDeclarationsModuleMutation,
    type UpdateConsentsAndDeclarationsModuleMutationVariables,
} from '../../../../api/mutations/updateConsentsAndDeclarationsModule';
import { type ConsentsAndDeclarationsModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type ConsentsModuleUpdateFormProps = {
    module: ConsentsAndDeclarationsModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = ConsentsAndDeclarationsModuleSettings;

const ConsentsModuleUpdateForm = ({ module, onComplete }: ConsentsModuleUpdateFormProps) => {
    const { t } = useTranslation(['consentsAndDeclarationsModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('consentsAndDeclarationsModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<
                    UpdateConsentsAndDeclarationsModuleMutation,
                    UpdateConsentsAndDeclarationsModuleMutationVariables
                >({
                    mutation: UpdateConsentsAndDeclarationsModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('consentsAndDeclarationsModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateConsentsModule"
                    layout="vertical"
                    name="updateConsentsModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('consentsAndDeclarationsModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default ConsentsModuleUpdateForm;
